import React from "react";

import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Gauge, gaugeClasses, useGaugeState } from "@mui/x-charts/Gauge";
import { useTheme, lighten, darken } from "@mui/material/styles";

import {
  interpolateDivergant,
  toCategoryName,
  toIconLabel,
  toIndex,
  toPercentage,
} from "@/utils";
import { Icon } from "@/components/atoms";

interface CategoryCardProps {
  category: string;
  value: number;
  score: string;
  type: string;
  minValue?: number;
  maxValue?: number;
  selectedCategory?: string;
  onSelect?: (category: string) => void;
  width?: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  value,
  score,
  type,
  minValue,
  maxValue,
  selectedCategory,
  onSelect,
  width = 230
}) => {
  const theme = useTheme();

  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const calcPercentage = (value: number) => {
    const mx = maxValue ? maxValue : 1;
    const mn = minValue ? minValue : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  function GaugeIcon({ category }: { category: string }) {
    const { valueAngle, cx, cy } = useGaugeState();

    if (valueAngle === null) {
      // No value to display
      return null;
    }

    return (
      <g transform={`translate(${cx},${cy}) scale(1)`}>
        <g transform="translate(-12, -14)">
          <Icon type={type} name={category} color="inherit" />
        </g>
      </g>
    );
  }

  return (
    <Grid key={category}>
      <Card
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor:
            category === selectedCategory
              ? theme.palette.primary.main
              : theme.palette.divider,
          width: width,
          height: "166px",
        }}
      >
        <CardActionArea
          disabled={!onSelect}
          onClick={() => (onSelect ? onSelect(category) : null)}
        >
          <CardContent
            sx={{
              pb: 2,
              "&:last-child": { pb: 2 },
            }}
          >
            <Stack alignItems="center">
              <Typography
                sx={{
                  color: interpolateDivergant(
                    theme.palette.score.light,
                    theme.palette.score.main,
                    theme.palette.score.dark,
                    1 - calcPercentage(value),
                  ),
                }}
              >
                {formatFunc(value)}
              </Typography>
              <Gauge
                width={100}
                height={100}
                value={Math.max(value, minValue ? minValue : 0)}
                valueMin={minValue ? minValue : 0}
                valueMax={maxValue ? maxValue : 1}
                text={""}
                startAngle={-110}
                endAngle={110}
                innerRadius="80%"
                outerRadius="100%"
                cornerRadius="50%"
                sx={{
                  [`& .${gaugeClasses.valueArc}`]: {
                    // fill: darken(theme.palette.secondary.main, (1 - calcPercentage(value)) / 1.5),
                    fill: interpolateDivergant(
                      theme.palette.score.light,
                      theme.palette.score.main,
                      theme.palette.score.dark,
                      1 - calcPercentage(value),
                    ),
                  },
                  [`& .${gaugeClasses.referenceArc}`]: {
                    fill: theme.palette.mode === 'dark'
                      ? lighten(theme.palette.background.paper, 0.14)
                      : darken(theme.palette.background.paper, 0.14),
                  },
                  margin: -1,
                }}
              >
                <GaugeIcon category={toIconLabel(category)} />
              </Gauge>
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {toCategoryName(category)}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

export default CategoryCard;
